import React, { useState } from 'react';
import './JobsList.css';
import { videoService } from '../services/api';

function JobsList({ jobs, onDeleteJob }) {
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [previewJobId, setPreviewJobId] = useState(null);

  const jobsArray = Object.entries(jobs).map(([jobId, jobData]) => ({
    jobId,
    ...jobData
  }));

  // Sort by created_at (most recent first)
  const sortedJobs = jobsArray.sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const handleDelete = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      setDeletingJobId(jobId);
      try {
        await onDeleteJob(jobId);
      } catch (error) {
        alert('Failed to delete job');
      } finally {
        setDeletingJobId(null);
      }
    }
  };

  const handleDownload = (jobId) => {
    const downloadUrl = videoService.downloadVideo(jobId);
    window.open(downloadUrl, '_blank');
  };

  const handlePreview = (jobId) => {
    setPreviewJobId(previewJobId === jobId ? null : jobId);
  };

  const handleClosePreview = () => {
    setPreviewJobId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'generating':
      case 'processing':
        return 'processing';
      default:
        return '';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'fa-check-circle';
      case 'failed':
        return 'fa-times-circle';
      case 'generating':
      case 'processing':
        return 'fa-spinner fa-spin';
      default:
        return 'fa-clock';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (sortedJobs.length === 0) {
    return (
      <div className="jobs-list">
        <div className="header">
          <h1>All Jobs</h1>
          <p className="subtitle">Monitor and manage your video generation jobs</p>
        </div>
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>No jobs yet</h3>
          <p>Start generating videos to see them here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jobs-list">
      <div className="header">
        <h1>All Jobs</h1>
        <p className="subtitle">
          {sortedJobs.length} total job{sortedJobs.length !== 1 ? 's' : ''}
        </p>
      </div>

      <div className="jobs-grid">
        {sortedJobs.map((job) => (
          <div key={job.jobId} className={`job-card ${getStatusColor(job.status)}`}>
            <div className="job-header">
              <div className="job-status">
                <i className={`fas ${getStatusIcon(job.status)}`}></i>
                <span className="status-text">
                  {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                </span>
              </div>
              <button
                className="xbtn xbtn-icon xbtn-danger delete-btn"
                onClick={() => handleDelete(job.jobId)}
                disabled={deletingJobId === job.jobId}
                title="Delete job"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>

            <div className="job-id">
              <strong>Job ID:</strong>
              <span>{job.jobId}</span>
            </div>

            {job.progress !== undefined && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${job.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{job.progress}%</span>
              </div>
            )}

            <div className="job-message">
              {job.message || 'No message'}
            </div>

            {job.error && (
              <div className="job-error">
                <i className="fas fa-exclamation-triangle"></i>
                {job.error}
              </div>
            )}

            <div className="job-footer">
              <div className="job-date">
                <i className="fas fa-calendar"></i>
                {formatDate(job.created_at)}
              </div>

              {job.status === 'completed' && job.video_path && (
                <div className="job-actions">
                  <button
                    className="xbtn xbtn-sm xbtn-accent preview-btn"
                    onClick={() => handlePreview(job.jobId)}
                  >
                    <i className={`fas fa-${previewJobId === job.jobId ? 'times' : 'play'}`}></i>
                    {previewJobId === job.jobId ? 'Close' : 'Preview'}
                  </button>
                  <button
                    className="xbtn xbtn-sm xbtn-primary download-btn"
                    onClick={() => handleDownload(job.jobId)}
                  >
                    <i className="fas fa-download"></i>
                    Download
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {previewJobId && (
        <div className="preview-overlay" onClick={handleClosePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="xbtn xbtn-icon xbtn-secondary close-preview" onClick={handleClosePreview}>
              <i className="fas fa-times"></i>
            </button>
            <video
              controls
              autoPlay
              className="modal-video"
              src={videoService.getVideoUrl(sortedJobs.find(j => j.jobId === previewJobId))}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobsList;
